import { useCallback, useMemo } from 'react'
import { useLocalStorage } from './useLocalStorage'

/**
 * localStorage key holding the flat list of hunt ids the user chose to remove
 * from their OWN list. One flat set covers both card kinds — an upcoming-hunt
 * id and an ended-hunt id never collide, so they share the store. This mirrors
 * the mobile app's `shared_preferences` key: the choice is per-device by
 * design, so a hide on web is independent of a hide on mobile.
 */
export const HIDDEN_HUNTS_KEY = 'wehunt.hiddenHuntIds'

export interface HiddenHunts {
  /** The current set of hidden hunt ids. */
  hiddenIds: Set<string>
  isHidden: (id: string) => boolean
  /** Removes a hunt from this user's list on this device (non-destructive). */
  hide: (id: string) => void
  /** Restores a previously removed hunt. */
  unhide: (id: string) => void
}

/**
 * Per-user, DEVICE-LOCAL registry of hunt cards the user removed from their own
 * hunt list. Removing a card is deliberately NON-DESTRUCTIVE: it does not
 * decline an RSVP, cancel the hunt for the team, or delete anything — the hunt
 * stays visible to everyone else. That is why the state lives in localStorage
 * (via {@link useLocalStorage}, which is already cross-instance synced) rather
 * than on the server: the only thing that changes is "this device does not want
 * to see this card", which has no meaning to any other reader.
 */
export function useHiddenHunts(): HiddenHunts {
  const [ids, setIds] = useLocalStorage<string[]>(HIDDEN_HUNTS_KEY, [])
  const hiddenIds = useMemo(() => new Set(ids), [ids])

  const hide = useCallback(
    (id: string) => setIds((prev) => (prev.includes(id) ? prev : [...prev, id])),
    [setIds],
  )
  const unhide = useCallback(
    (id: string) => setIds((prev) => prev.filter((x) => x !== id)),
    [setIds],
  )
  const isHidden = useCallback((id: string) => hiddenIds.has(id), [hiddenIds])

  return { hiddenIds, isHidden, hide, unhide }
}
